import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArticleCard from '../components/ui/ArticleCard.jsx';
import IssueCard from '../components/ui/IssueCard.jsx';
import Placeholder from '../components/ui/Placeholder.jsx';
import SectionHead from '../components/ui/SectionHead.jsx';
import Button from '../components/ui/Button.jsx';
import { getStoredAuth, listArticles, listIssues } from '../lib/auth';
import { normalizeArticle, normalizeIssue } from '../lib/journalFormat';

const EDITORIAL_MEMBERS = [
  {
    id: 1,
    name: 'Dr Sugandha Jauhari',
    credentials: 'MD',
    role: 'Editor-in-Chief',
    specialty: 'Community Medicine & Public Health, KGMU, Lucknow',
    initials: 'SJ',
  },
  {
    id: 2,
    name: 'Dr Mohit K Srivastava',
    credentials: 'MD, DNB, DM (Pain Medicine), MNAMS',
    role: 'Director — Editorial Operations',
    specialty: 'Physical Medicine & Rehabilitation, VMMC & SJH, New Delhi',
    initials: 'MS',
  },
  {
    id: 3,
    name: 'Dr Shubham Dadoo',
    credentials: 'MBBS, MS (ENT)',
    role: 'Editorial Board',
    specialty: 'ENT–HNS, Optima Hospital, Meerut',
    initials: 'SD',
  },
];

function AvatarPlaceholder({ initials = 'ED', size = 'md' }) {
  const sizeClass = size === 'lg' ? 'w-20 h-20 text-2xl' : 'w-14 h-14 text-lg';
  return (
    <div
      className={`${sizeClass} rounded-full bg-slate-pale flex items-center justify-center font-display font-bold text-slate flex-shrink-0`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function Home() {
  const [articles, setArticles] = useState([]);
  const [issues, setIssues] = useState([]);
  const [message, setMessage] = useState('');
  const auth = getStoredAuth();
  const canSubmit = auth?.user?.role === 'author';

  useEffect(() => {
    document.title = 'JAMSD — Journal of Applied Medical and Surgical Disciplines';
    Promise.all([listArticles({ limit: 3 }), listIssues()])
      .then(([articleResult, issueResult]) => {
        setArticles((articleResult.data || []).slice(0, 3).map(normalizeArticle));
        setIssues((issueResult.data || []).slice(0, 3).map(normalizeIssue));
      })
      .catch((error) => setMessage(error.message));
  }, []);

  const latestIssue = issues.find((issue) => !issue.upcoming) || issues[0];

  return (
    <>
      {/* Hero / Masthead */}
      <section className="bg-navy pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left */}
            <div className="flex gap-5">
              <div className="w-1 min-h-full bg-amber rounded-full flex-shrink-0" aria-hidden="true" />
              <div className="flex flex-col gap-5">
                <div>
                  <span className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2 block">
                    International · Peer-Reviewed · Open Access
                  </span>
                  <h1 className="font-display text-white text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                    Journal of Applied Medical and Surgical Disciplines
                  </h1>
                </div>
                <p className="font-sans text-slate-light text-base md:text-lg leading-relaxed max-w-xl">
                  Advancing Evidence-Based Research Across Medical and Surgical Disciplines
                </p>
                <p className="font-sans text-slate-pale text-sm leading-relaxed max-w-lg">
                  ISSN: XXXX-XXXX &mdash; Biannual, Open Access (CC BY 4.0)
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button to="/articles" variant="solid" color="amber" size="lg">
                    Browse Articles
                  </Button>
                  {canSubmit && (
                    <Button to="/submit" variant="outline" color="amber" size="lg">
                      Submit Manuscript
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Latest Issue card */}
            <div className="flex justify-center lg:justify-end">
              <div className="bg-white rounded-xl shadow-xl overflow-hidden max-w-sm w-full">
                <div className="bg-navy-medium px-5 py-4 border-b-2 border-amber">
                  <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest">
                    Latest Issue
                  </p>
                </div>
                <div className="p-5 flex flex-col gap-3">
                  <div className="flex items-start gap-4">
                    <div className="bg-bg-ice rounded-lg p-3 flex-shrink-0">
                      <svg className="w-8 h-8 text-slate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-display text-navy font-bold text-lg">{latestIssue ? `${latestIssue.volume}, Issue ${latestIssue.issueNumber}` : 'Latest Issue'}</h3>
                      <p className="font-sans text-slate text-sm">{latestIssue ? `${latestIssue.season} ${latestIssue.year}` : 'No issue configured yet'}</p>
                      <span className="inline-block mt-1.5 text-xs font-sans font-semibold bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        {latestIssue?.upcoming ? 'Upcoming' : 'Published'}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-bg-border pt-3">
                    <p className="font-sans text-xs text-slate italic">
                      {latestIssue ? `${latestIssue.articleCount} ${latestIssue.articleCount === 1 ? 'article' : 'articles'}` : 'Articles coming soon'}
                    </p>
                  </div>
                  <div className="border-t border-bg-border pt-3">
                    <p className="font-sans text-xs text-gray-500 leading-relaxed">
                      <span className="font-semibold">Scope:</span> Clinical Practice · Clinical Trials · Broad Medical Specialities · Epidemiology &amp; Public Health
                    </p>
                  </div>
                  <Link
                    to={latestIssue ? `/issues/${latestIssue.id}` : '/issues'}
                    className="inline-flex items-center gap-1 text-amber font-sans text-sm font-semibold hover:text-amber-dark transition-colors"
                  >
                    View all issues
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Aims & Scope Strip */}
      <section className="bg-bg-ice border-b border-bg-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-1 h-8 bg-amber rounded-full" aria-hidden="true" />
              <h2 className="font-display text-navy font-bold text-lg">Aims &amp; Scope</h2>
            </div>
            <p className="font-sans text-gray-700 text-sm md:text-base leading-relaxed flex-1">
              JAMSD is an international, peer-reviewed, open-access e-journal committed to high-quality, multidisciplinary research
              in medical and health sciences — bridging biomedical sciences, clinical practice, epidemiology, health systems,
              military medicine, medical education, digital health, and global health.
            </p>
            <Link
              to="/aims-and-scope"
              className="flex-shrink-0 inline-flex items-center gap-1 font-sans text-sm font-semibold text-amber hover:text-amber-dark transition-colors whitespace-nowrap"
            >
              Read Full Aims &amp; Scope
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* EDGE Highlight */}
      <section className="bg-navy border-b border-navy-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border-2 border-amber p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex-1">
                {/* <span className="inline-block bg-amber text-navy text-xs font-sans font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-3">
                  Special Section
                </span> */}
                <h2 className="font-display text-white text-2xl md:text-3xl font-bold mb-2">
                  Introducing EDGE: Evidence, Debate, Guidance &amp; Experience
                </h2>
                <p className="font-sans text-slate-light text-sm md:text-base leading-relaxed max-w-3xl">
                  Your razor-sharp gateway to transforming research into real-world impact. EDGE cuts through the
                  noise with provocative, bite-sized symposia — incisive evidence breakdowns, fiery debates,
                  practical guidance, and candid frontline experience — delivered in accessible, open-access formats.
                </p>
              </div>
              <Link
                to="/aims-and-scope"
                className="flex-shrink-0 inline-flex items-center gap-1 font-sans text-sm font-semibold text-amber hover:text-white transition-colors whitespace-nowrap"
              >
                Learn More About EDGE
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-14 bg-bg-cool">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <SectionHead title="Latest Articles" />
            <Link
              to="/articles"
              className="flex-shrink-0 font-sans text-sm font-semibold text-amber hover:text-amber-dark transition-colors flex items-center gap-1"
            >
              View all articles
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
          {message && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}
          {!articles.length && !message && <Placeholder label="No published articles yet" />}
        </div>
      </section>

      {/* Browse by Issue */}
      <section className="py-14 bg-bg-ice border-t border-bg-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <SectionHead title="Browse by Issue" />
            <Link
              to="/issues"
              className="flex-shrink-0 font-sans text-sm font-semibold text-amber hover:text-amber-dark transition-colors flex items-center gap-1"
            >
              All issues
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {issues.map((issue) => (
              <IssueCard key={issue.id} issue={issue} />
            ))}
          </div>
          {!issues.length && !message && <Placeholder label="No issues configured yet" />}
        </div>
      </section>

      {/* Submit CTA Banner */}
      <section className="bg-navy py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-white text-3xl md:text-4xl font-bold mb-4">
              Share Your Research With the World
            </h2>
            <p className="font-sans text-slate-light text-base md:text-lg leading-relaxed mb-8">
              JAMSD welcomes original research articles, reviews, commentaries, case reports, and methodological innovations
              from across diverse fields in medical and health sciences. We publish biannually with no article processing charges.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button to="/author-guidelines" variant="solid" color="amber" size="lg">
                Review Author Guidelines
              </Button>
              {canSubmit && (
                <Button to="/submit" variant="outline" color="amber" size="lg">
                  Submit Manuscript
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Board Preview */}
      <section className="py-14 bg-bg-cool">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
            <SectionHead title="Editorial Board" />
            <Link
              to="/editorial-board"
              className="flex-shrink-0 font-sans text-sm font-semibold text-amber hover:text-amber-dark transition-colors flex items-center gap-1"
            >
              Meet the full board
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {EDITORIAL_MEMBERS.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-lg border border-bg-border shadow-sm p-5 flex items-center gap-4"
              >
                <AvatarPlaceholder initials={member.initials} />
                <div>
                  <h3 className="font-display text-navy font-semibold text-base leading-tight">
                    {member.name}
                  </h3>
                  <p className="font-sans text-slate text-xs mt-0.5">{member.credentials}</p>
                  <p className="font-sans text-amber text-xs font-semibold mt-1">{member.role}</p>
                  <p className="font-sans text-gray-500 text-xs mt-0.5">{member.specialty}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
